/**
 * Shared sim-overlay layout lookups — built once per overlay mount so each
 * OHT vehicle does not re-subscribe to the full layout/equipment Redux trees.
 */
import React, { createContext, useContext, useMemo, useRef } from 'react';
import { useAppSelector } from '../../hooks/redux';
import {
  buildStationPortIndex,
  type StationPortIndex,
} from './amhs/Equipment/stationPortIndex';
import type { LayoutElement } from '../../types';
import type { EquipmentInstance, EquipmentTemplate } from '../../features/equipment/equipmentTypes';
import type {
  AmhsFinalSimulationLeg,
  BrowserKineticIdleVehicle,
  VehiclePlayback,
} from '../../types/simulationPlayback';
import { indexApiLegsByOht, shortEndpointLabel } from '../../utils/simulationPhaseUi';

const EMPTY_API_LEGS: AmhsFinalSimulationLeg[] = [];
const EMPTY_IDLE_BY_VEHICLE: Record<string, BrowserKineticIdleVehicle> = {};

export type SimOverlayShared = {
  simPaused: boolean;
  source: string | null | undefined;
  apiLegsByOhtRef: React.MutableRefObject<Map<string, AmhsFinalSimulationLeg[]>>;
  elements: Record<string, LayoutElement>;
  equipmentInstances: Record<string, EquipmentInstance>;
  equipmentTemplates: Record<string, EquipmentTemplate>;
  stationIndex: StationPortIndex;
  gatePointByPortId: Map<string, { x: number; y: number }>;
  stockerPortOwners: Record<string, string> | undefined;
  toolPortOwners: Record<string, string> | undefined;
  playbackVehiclesRef: React.MutableRefObject<Record<string, VehiclePlayback>>;
  idleByVehicleRef: React.MutableRefObject<Record<string, BrowserKineticIdleVehicle>>;
  endpointNames: Record<string, string>;
  requestStationsById: Record<string, { pickup: string; drop: string }>;
};

const SimOverlaySharedContext = createContext<SimOverlayShared | null>(null);

export function useSimOverlayShared(): SimOverlayShared {
  const ctx = useContext(SimOverlaySharedContext);
  if (!ctx) {
    throw new Error('useSimOverlayShared must be used under SimOverlaySharedProvider');
  }
  return ctx;
}

export const SimOverlaySharedProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const simPaused = useAppSelector((state) => state.simulation.paused);
  const source = useAppSelector((state) => state.simulation.source);
  const apiLegs = useAppSelector(
    (state) => state.simulation.browserKinetic?.apiLegs ?? EMPTY_API_LEGS
  );
  const playbackVehicles = useAppSelector((state) => state.simulation.playbackVehicles);
  const idleByVehicle = useAppSelector(
    (state) =>
      state.simulation.browserKinetic?.vehicleLegs.idleByVehicle ?? EMPTY_IDLE_BY_VEHICLE
  );
  const elements = useAppSelector((state) => state.layout.elements);
  const stockerPortOwners = useAppSelector(
    (state) => state.simulation.browserKinetic?.stockerPortOwners
  );
  const toolPortOwners = useAppSelector(
    (state) => state.simulation.browserKinetic?.toolPortOwners
  );
  const equipmentInstances = useAppSelector((state) => state.equipment.instances);
  const equipmentTemplates = useAppSelector((state) => state.equipment.templates);
  const liveGates = useAppSelector((state) => state.simulation.live.inventory?.gates);
  const transportRequests = useAppSelector((state) => state.simulation.transportRequests);
  const apiLegsByOht = useMemo(() => indexApiLegsByOht(apiLegs), [apiLegs]);

  const endpointNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const element of Object.values(elements)) {
      const ownerName =
        element.metadata.name?.trim() ||
        String(element.customProperties?.amhsId ?? '').trim() ||
        '';
      if (!ownerName) continue;
      const compact = ownerName.slice(0, 24);
      for (const key of [
        element.id,
        String(element.customProperties?.amhsId ?? ''),
        String(element.customProperties?.toolId ?? ''),
        String(element.customProperties?.dxfEquipmentId ?? ''),
        element.metadata.name,
      ]) {
        if (key) names[key] = compact;
      }
      const ports = element.customProperties?.ports;
      if (!Array.isArray(ports)) continue;
      for (const port of ports as Array<{ id?: string; name?: string }>) {
        if (!port.id) continue;
        names[port.id] = (port.name?.trim() || compact).slice(0, 24);
      }
    }
    return names;
  }, [elements]);

  const requestStationsById = useMemo(() => {
    const stations: Record<string, { pickup: string; drop: string }> = {};
    for (const request of transportRequests) {
      const pickup = shortEndpointLabel(
        request.pickupPort || request.pickupStocker || request.pickupToolId || request.pickup,
        endpointNames
      );
      const drop = shortEndpointLabel(
        request.dropPort ||
          request.destinationStocker ||
          request.dropToolId ||
          request.drop,
        endpointNames
      );
      for (const id of [request.transportRequestId, request.requestId]) {
        if (id) stations[id] = { pickup, drop };
      }
    }
    return stations;
  }, [endpointNames, transportRequests]);
  const apiLegsByOhtRef = useRef(apiLegsByOht);
  const playbackVehiclesRef = useRef(playbackVehicles);
  const idleByVehicleRef = useRef(idleByVehicle);
  apiLegsByOhtRef.current = apiLegsByOht;
  playbackVehiclesRef.current = playbackVehicles;
  idleByVehicleRef.current = idleByVehicle;

  const stationIndex = useMemo(
    () =>
      buildStationPortIndex(elements, equipmentInstances, equipmentTemplates, [
        stockerPortOwners,
        toolPortOwners,
      ]),
    [elements, equipmentInstances, equipmentTemplates, stockerPortOwners, toolPortOwners]
  );

  const gatePointByPortId = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>();
    for (const gate of liveGates ?? []) {
      if (
        typeof gate.x === 'number' &&
        Number.isFinite(gate.x) &&
        typeof gate.y === 'number' &&
        Number.isFinite(gate.y)
      ) {
        map.set(gate.portId, { x: gate.x, y: gate.y });
      }
    }
    return map;
  }, [liveGates]);

  const value = useMemo(
    () => ({
      simPaused,
      source,
      apiLegsByOhtRef,
      elements,
      equipmentInstances,
      equipmentTemplates,
      stationIndex,
      gatePointByPortId,
      stockerPortOwners,
      toolPortOwners,
      playbackVehiclesRef,
      idleByVehicleRef,
      endpointNames,
      requestStationsById,
    }),
    [
      simPaused,
      source,
      apiLegsByOhtRef,
      elements,
      equipmentInstances,
      equipmentTemplates,
      stationIndex,
      gatePointByPortId,
      stockerPortOwners,
      toolPortOwners,
      playbackVehiclesRef,
      idleByVehicleRef,
      endpointNames,
      requestStationsById,
    ]
  );

  return (
    <SimOverlaySharedContext.Provider value={value}>
      {children}
    </SimOverlaySharedContext.Provider>
  );
};
